import copy from '../copy/en/office-context.json' with { type: 'json' };

export interface OfficeContextState {
  practiceName: string;
  address: string;
  repeated: boolean;
  acknowledged: boolean;
  preview: boolean;
  visible: boolean;
  disabled: boolean;
}

interface Options {
  surface: HTMLElement;
  onEditLocation(): void;
  onReviewMatch(): void;
}

/** Signer-owned state; native sticky positioning runs in the document that scrolls. */
export function createOfficeContext({ surface, onEditLocation, onReviewMatch }: Options) {
  const doc = surface.ownerDocument;
  const win = doc.defaultView!;
  const slot = doc.createElement('div'); slot.className = 'office-context-slot'; slot.hidden = true;
  const card = doc.createElement('section'); card.className = 'office-context'; card.setAttribute('aria-label', copy.label);
  const pin = doc.createElement('span'); pin.className = 'office-context__pin'; pin.setAttribute('aria-hidden', 'true');
  pin.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="M19 10c0 5-7 11-7 11S5 15 5 10a7 7 0 1 1 14 0Z"/><circle cx="12" cy="10" r="2.5"/></svg>';
  const identity = doc.createElement('div'); identity.className = 'office-context__identity';
  const name = doc.createElement('strong'); name.className = 'office-context__name';
  const address = doc.createElement('span'); address.className = 'office-context__address';
  const status = doc.createElement('span'); status.className = 'office-context__status';
  identity.append(name, address, status);
  const actions = doc.createElement('div'); actions.className = 'office-context__actions';
  const edit = doc.createElement('button'); edit.type = 'button'; edit.textContent = copy.editLocation;
  const review = doc.createElement('button'); review.type = 'button'; review.textContent = copy.reviewMatch;
  actions.append(edit, review); card.append(pin, identity, actions); slot.append(card); surface.prepend(slot);
  let state: OfficeContextState | undefined;
  let disposed = false;
  let pending: number | undefined;
  let host: Element | null = null;
  let container: HTMLElement | null = null;
  // Only the known, same-origin invitation can host the presentation layer.
  try {
    if (win.parent !== win && win.parent.location.origin === win.location.origin) {
      const frame = win.frameElement;
      if (frame?.matches('.provider-frame--native') && frame.parentElement?.id === 'embed-container') {
        host = frame; container = frame.parentElement;
      }
    }
  } catch { /* Cross-origin embedding uses the child viewport only. */ }
  const layer = container ? container.ownerDocument.createElement('div') : null;
  const layoutWindow = container?.ownerDocument.defaultView ?? win;
  if (layer && container) {
    layer.className = 'office-context-layer'; layer.hidden = true;
    container.classList.add('office-context-host');
    layer.append(card); container.append(layer);
    slot.classList.add('office-context-slot--reserved');
    // Preserve the signer's theme without importing its page styles into the host.
    const theme = win.getComputedStyle(surface);
    for (const property of ['--surface', '--surface-raised', '--text', '--muted', '--line', '--gold', '--gold-line', '--gold-bright', '--gold-soft', '--success']) {
      layer.style.setProperty(property, theme.getPropertyValue(property));
    }
    layer.style.fontFamily = theme.fontFamily;
  }
  const listeners: (() => void)[] = [];
  const listen = (target: EventTarget, event: string, callback: EventListener = schedule) => {
    target.addEventListener(event, callback, { passive: true });
    listeners.push(() => target.removeEventListener(event, callback));
  };
  const rail = doc.querySelector<HTMLElement>('.progress-rail');
  function position() {
    pending = undefined;
    if (disposed) return;
    if (layer && host && container) {
      layer.hidden = !state?.visible || !host.isConnected || host.hasAttribute('hidden')
        || !host.matches('.provider-frame--native') || !host.getClientRects().length
        || !!doc.querySelector('dialog[open], .field-popup:not([hidden])');
      if (layer.hidden) return;
      const frame = host.getBoundingClientRect();
      const bounds = container.getBoundingClientRect();
      const form = surface.getBoundingClientRect();
      // Parent scroll cancels out: these are stable container-relative bounds.
      layer.style.left = `${frame.left - bounds.left + form.left + surface.clientLeft}px`;
      layer.style.top = `${frame.top - bounds.top + form.top + surface.clientTop}px`;
      layer.style.width = `${surface.clientWidth || form.width}px`;
      layer.style.height = `${surface.clientHeight || form.height}px`;
      layer.classList.toggle('office-context-layer--compact', win.innerWidth <= 540);
      const height = `${card.getBoundingClientRect().height}px`;
      if (slot.style.height !== height) slot.style.height = height;
      return;
    }
    if (!state?.visible) return;
    let top = 0;
    if (rail && win.getComputedStyle(rail).position === 'sticky') {
      const rect = rail.getBoundingClientRect(), form = surface.getBoundingClientRect();
      // Only the horizontal mobile rail shares the form's viewport column.
      if (rect.right > form.left && rect.left < form.right) top = rect.height;
    }
    slot.style.top = `${top}px`;
  }
  function schedule() {
    if (!disposed && pending === undefined) pending = layoutWindow.requestAnimationFrame(position);
  }
  const editLocation = () => { if (!disposed && state?.visible && !state.disabled) onEditLocation(); };
  const reviewMatch = () => { if (!disposed && state?.visible && state.repeated && !state.disabled) onReviewMatch(); };
  edit.addEventListener('click', editLocation); review.addEventListener('click', reviewMatch);
  // No scroll listener or per-scroll transform: the browser pins the badge.
  listen(win, 'resize');
  if (container) listen(win.parent, 'resize');
  listen(win, 'pagehide', event => {
    if ((event as PageTransitionEvent).persisted) layer?.remove();
    else dispose();
  });
  listen(win, 'pageshow', () => { if (layer && container && !disposed) { container.append(layer); schedule(); } });
  const observer = typeof win.ResizeObserver === 'function' ? new win.ResizeObserver(schedule) : undefined;
  observer?.observe(surface); observer?.observe(card); observer?.observe(doc.body);
  if (rail) observer?.observe(rail); if (host) observer?.observe(host);
  const hostObserver = host && typeof win.MutationObserver === 'function' ? new win.MutationObserver(schedule) : undefined;
  if (host) hostObserver?.observe(host, { attributes: true, attributeFilter: ['hidden', 'style', 'class'] });
  if (container) hostObserver?.observe(container, { childList: true });
  const modalObserver = layer && typeof win.MutationObserver === 'function' ? new win.MutationObserver(schedule) : undefined;
  modalObserver?.observe(doc.body, { subtree: true, attributes: true, attributeFilter: ['open', 'hidden'], childList: true });
  function dispose() {
    if (disposed) return;
    disposed = true;
    if (pending !== undefined) layoutWindow.cancelAnimationFrame(pending);
    observer?.disconnect(); hostObserver?.disconnect(); modalObserver?.disconnect(); listeners.forEach(remove => remove());
    edit.removeEventListener('click', editLocation); review.removeEventListener('click', reviewMatch);
    slot.remove(); layer?.remove();
    if (container && !container.querySelector('.office-context-layer')) container.classList.remove('office-context-host');
  }
  return {
    update(next: OfficeContextState) {
      if (disposed) return;
      state = { ...next };
      name.textContent = next.practiceName.trim() || copy.practicePlaceholder;
      address.textContent = next.address.trim() || copy.addressPlaceholder;
      status.textContent = next.preview ? copy.previewMatch : next.acknowledged ? copy.acknowledged : copy.repeated;
      status.hidden = !next.repeated; review.hidden = !next.repeated;
      card.classList.toggle('has-match', next.repeated);
      card.classList.toggle('is-confirmed', next.repeated && next.acknowledged);
      edit.disabled = next.disabled; review.disabled = next.disabled;
      slot.hidden = !next.visible;
      if (!next.visible && layer) layer.hidden = true;
      schedule();
    },
    dispose,
  };
}
